import { useEffect, type RefObject } from "react";

/** Height of the global search strip the page header sticks under. */
const SEARCH_STRIP_HEIGHT = 56;

/**
 * Keeps the sticky page header informed of its situation: `data-stuck` once
 * content scrolls underneath it, and `--page-sticky-offset` on the page so
 * nested sticky regions (list toolbars…) stack right below it.
 */
export function usePinnedHeader(headerRef: RefObject<HTMLElement | null>, canvas: boolean) {
  useEffect(() => {
    const header = headerRef.current;
    const scroller = header?.closest("main");
    if (!header || !scroller) return;
    const syncStuck = () =>
      header.toggleAttribute("data-stuck", canvas ? scroller.scrollTop > 0 : window.scrollY > 0);
    const syncOffset = () =>
      scroller.style.setProperty(
        "--page-sticky-offset",
        `${header.offsetHeight + (canvas ? 0 : SEARCH_STRIP_HEIGHT)}px`,
      );
    syncStuck();
    syncOffset();
    const scrollTarget = canvas ? scroller : window;
    scrollTarget.addEventListener("scroll", syncStuck, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(syncOffset);
    observer?.observe(header);
    return () => {
      scrollTarget.removeEventListener("scroll", syncStuck);
      observer?.disconnect();
      scroller.style.removeProperty("--page-sticky-offset");
    };
  }, [headerRef, canvas]);
}
